import React, {
	useMemo,
	useContext,
	useEffect,
	useRef,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Socket, { ConnectedUser } from '../../connection/socket';
import { OpenTokContext } from '../../contexts/opentok/opentok-context';
import { setMetaMenuRoute, setMetaMenuState } from '../../stores/actions/session';
import { Invitation } from '../../stores/reducers/invitations';
import { META_MENU_ROUTES, META_MENU_STATES, RESTRICTED_USER_STATES } from '../../stores/reducers/session';
import { ROOM_STATES } from '../../stores/reducers/settings';
import { GreenroomState } from '../../stores/types/types';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { VideoStream } from '../types/videos';
import RestrictedUserThumbnail from './restricted-user-thumbnail';
import VideoFeedFakeThumbnail from './video-feed-fake-thumbnail';
import VideoFeedThumbnail from './video-feed-thumbnail';

interface Props {
	className?: string;
	socket?: Socket | null;
}

export default function FeedTab(props: Props) {
	const dispatch = useDispatch();

	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const restrictedUserState = useSelector(
		(state: GreenroomState) => state.SessionReducer.restrictedUserState,
	);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const connectedPresenterVideos = useSelector(
		(state: GreenroomState) => state.VideoReducer.connectedPresenterVideos,
	);
	const invitations = useSelector((state: GreenroomState) => state.InvitationsReducer.invitations);
	const roomState = useSelector((state: GreenroomState) => state.MeetingReducer.roomState);

	const {
		className,
		socket,
	} = props;

	const { opentok } = useContext(OpenTokContext);

	const feedsRef = useRef<HTMLDivElement | null>(null);

	const invitePresenters = () => {
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.SHARING));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
	};

	const preconnectVideo = useMemo(() => {
		if (!opentok?.session?.connection && restrictedUserState === RESTRICTED_USER_STATES.NONE) {
			return connectedPresenterVideos.find((vid: VideoStream) => vid.self || vid.local);
		}
		return undefined;
	}, [opentok, restrictedUserState, connectedPresenterVideos]);

	const restrictedUsers = useMemo(() => connectedUsers.filter(
		(u: ConnectedUser) => u.in_room
		&& u.restrictedUserState
		&& u.restrictedUserState > 2 // cam only and mic only restricted users show as presenter feeds
		&& !u.watcher_only,
	), [connectedUsers]);

	const invitedUsersNotInRoom = useMemo(() => {
		if (roomState === ROOM_STATES.EDITING && !embed) {
			// create fake feed for all invites that aren't in the room
			return invitations.filter(
				(invitation: Invitation) => !connectedUsers.some(
					(user: any) => user.gr_user === invitation.profile.gr_user && user?.in_room,
				) && !invitation.watcher_only,
			);
		}
		return [];
	}, [connectedUsers, invitations]);

	const renderedPresenterVideos = useMemo(() => {
		const newVideos = [...connectedPresenterVideos];
		// If you are the host, your thumbnail should always be last
		if (host) {
			const producerVideoIndex = newVideos.findIndex((vid) => vid.self);
			// Check if host thumbnail is last
			if (producerVideoIndex !== newVideos.length - 1) {
				newVideos.push(
					newVideos.splice(producerVideoIndex, 1)[0],
				);
			}
		}
		return newVideos;
	}, [host, connectedPresenterVideos]);

	return (
		<>
			<div
				className="restricted-feeds-tooltip"
				id="feeds-restricted-tooltip-background"
				ref={feedsRef}
			/>
			<div className={classNames('video-thumbs', className ?? '')} style={{ display: 'grid' }}>

				{/* only show all presenter videos when connected to OpenTok */}
				{opentok && <>
					{opentok?.session?.connection
						&& renderedPresenterVideos.map((vid: VideoStream, i: number) => <VideoFeedThumbnail
							video={vid}
							key={i}
							socket={socket}
							feedsRef={feedsRef}
						/>)}

					{/* show your own video when first connecting to OpenTok */}
					{!opentok?.session?.connection && restrictedUserState === RESTRICTED_USER_STATES.NONE
						&& <>
							{preconnectVideo && <VideoFeedThumbnail
								video={preconnectVideo}
								socket={socket}
								feedsRef={feedsRef}
							/>}
						</>
					}
				</>}

				{restrictedUsers.map((rUser: ConnectedUser, i: number) => <RestrictedUserThumbnail
					key={i}
					restrictedUser={rUser}
					feedsRef={feedsRef}
				/>)}

				{invitedUsersNotInRoom.map((invite: Invitation, i: number) => <VideoFeedFakeThumbnail
					invite={invite}
					key={i}
				/>)}

				{host && (
					<button className='video-thumb transparent border-dashed height-full' onClick={() => { invitePresenters(); }}>
						<div className='video-wrapper'></div>
						<Icon name={ICONS.ADD} size={14} color={COLORS.PALE_GREEN} className='margin-right-small' />
						<span className='text-green font-size-14 font-weight-heavy'>Invite</span>
					</button>
				)}

			</div>
		</>
	);
}
